/**
 * NewNegotiationDialog — start-negotiation flow (prep stages 3→5).
 *
 * Stage 3: Show the negotiation case (buildNegotiationCase output).
 * Stage 4: Review / edit suggested terms (suggestTerms output), pick a template.
 * Stage 5: Confirm recipients + subject, then submit.
 *
 * Props supply the case and suggestion so the dialog stays independently testable.
 * onSubmit is a pure callback — no edge-function wiring here (Wave E wires it).
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  Sparkles,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { discountToApr, formatDiscount } from "@/lib/negotiation/terms";
import { useNegotiationTemplates } from "@/hooks/useNegotiationTemplates";
import { useSettings } from "@/hooks/useSettings";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import type { NegotiationCase } from "@/lib/par/negotiationCase";
import type { SuggestedTerms } from "@/lib/negotiation/suggestTerms";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NewNegotiationSubmitPayload {
  /** Primary contact email. */
  supplier_email: string;
  supplier_name: string | null;
  /** Denormalized PAR vendor name. */
  vendor_name: string | null;
  subject: string;
  /** Discount-first terms. */
  target_discount_pct: number | null;
  target_days_early: number | null;
  floor_discount_pct: number | null;
  floor_apr: number | null;
  /** Derived from target_discount_pct + target_days_early for back-compat. */
  target_apr: number | null;
  /** Legacy field kept for back-compat; equals floor_apr. */
  max_acceptable_apr: number | null;
  tone: string | null;
  /** All recipient emails (primary + CCs). */
  recipients: string[];
}

export interface NewNegotiationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: NewNegotiationSubmitPayload) => Promise<void>;
  isSubmitting: boolean;
  /** Pre-filled primary contact email (e.g. from VendorContact). */
  defaultEmail?: string | null;
  defaultSupplierName?: string | null;
  /** PAR vendor_name to denormalize onto the thread. */
  defaultVendorName?: string | null;
  defaultTone?: string | null;
  /** Pre-built negotiation case from buildNegotiationCase (Stage 3). */
  negotiationCase?: NegotiationCase | null;
  /** Pre-built suggested terms from suggestTerms (Stage 4). */
  suggestedTerms?: SuggestedTerms | null;
  /**
   * List of selectable vendor names shown when no vendor is preselected.
   * Selecting one calls onVendorSelect so the parent can rebuild the case/terms.
   */
  vendorOptions?: string[];
  /**
   * Called when the user picks a vendor from the picker (no-preselect flow).
   * The parent should update negotiationCase / suggestedTerms / defaultEmail accordingly.
   */
  onVendorSelect?: (vendorName: string) => void;
  /**
   * The vendor's current agreed / PAR terms.
   * Shown as a "Current vs Proposed" comparison on the Terms step.
   *
   * - discount_pct / days_early: early-payment discount terms (if any)
   * - payment_terms: the net/due terms string, e.g. "Net 60", "COD" (if any)
   * - mixed_terms: all distinct term strings when the vendor uses more than one
   */
  currentTerms?: {
    discount_pct: number | null;
    days_early: number | null;
    payment_terms?: string | null;
    mixed_terms?: string[] | null;
  } | null;
}

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

const STEPS = ["Case", "Terms", "Send"] as const;
type Step = 0 | 1 | 2;

function StepIndicator({ step }: { step: Step }) {
  return (
    <ol className="flex items-center gap-1 mb-4">
      {STEPS.map((label, idx) => {
        const done = idx < step;
        const active = idx === step;
        return (
          <li key={label} className="flex items-center gap-1">
            <span
              className={cn(
                "inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold border",
                active && "bg-primary text-primary-foreground border-primary",
                done && "bg-primary/20 text-primary border-primary/40",
                !active && !done && "bg-muted text-muted-foreground border-border"
              )}
            >
              {done ? <Check className="w-2.5 h-2.5" /> : idx + 1}
            </span>
            <span
              className={cn(
                "text-xs",
                active ? "text-foreground font-medium" : "text-muted-foreground"
              )}
            >
              {label}
            </span>
            {idx < STEPS.length - 1 && (
              <ChevronRight className="w-3 h-3 text-muted-foreground/40 mx-0.5" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Helper: recipients tag input
// ---------------------------------------------------------------------------

function RecipientsInput({
  recipients,
  onChange,
}: {
  recipients: string[];
  onChange: (r: string[]) => void;
}) {
  const [inputVal, setInputVal] = useState("");

  const add = (raw: string) => {
    const val = raw.trim().toLowerCase();
    if (val && !recipients.includes(val)) {
      onChange([...recipients, val]);
    }
    setInputVal("");
  };

  const remove = (email: string) => {
    onChange(recipients.filter((r) => r !== email));
  };

  return (
    <div className="space-y-1.5">
      <Label htmlFor="nd-recipients-input" className="text-xs">
        Recipients *
      </Label>
      <div className="flex flex-wrap gap-1.5 min-h-[36px] rounded-md border border-input bg-background px-2 py-1.5">
        {recipients.map((r) => (
          <span
            key={r}
            className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary border border-primary/20 rounded px-1.5 py-0.5"
          >
            {r}
            <button
              type="button"
              aria-label={`Remove ${r}`}
              onClick={() => remove(r)}
              className="text-primary/60 hover:text-primary"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          id="nd-recipients-input"
          type="email"
          className="flex-1 min-w-[160px] bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
          placeholder={
            recipients.length === 0 ? "vendor@example.com — press Enter to add" : "Add another…"
          }
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(inputVal);
            }
          }}
          onBlur={() => {
            if (inputVal.trim()) add(inputVal);
          }}
        />
      </div>
      <p className="text-[11px] text-muted-foreground/60">
        Press Enter or comma to add each address. First is primary; additional are CC.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 3 — Case panel
// ---------------------------------------------------------------------------

function CasePanel({ negotiationCase }: { negotiationCase: NegotiationCase | null }) {
  if (!negotiationCase) {
    return (
      <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-2">
        <p className="text-sm text-muted-foreground italic">
          No case data supplied — you can still proceed.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
      <p className="text-sm font-medium text-foreground">{negotiationCase.headline}</p>
      {negotiationCase.reasons.length > 0 && (
        <ul className="space-y-2">
          {negotiationCase.reasons.map((r) => (
            <li key={r.key} className="flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-warning mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-foreground">{r.label}</p>
                <p className="text-[11px] text-muted-foreground">{r.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feature flags (internal)
// ---------------------------------------------------------------------------

/** Set to true to re-enable the templates picker in the Terms step. */
const SHOW_TEMPLATES = false;

// ---------------------------------------------------------------------------
// Tone field — Textarea with Tab-accept ghost-text autocomplete
// ---------------------------------------------------------------------------

/**
 * A textarea for the negotiation tone that supports Tab-accept autocomplete.
 * When the field is empty (or the user hasn't typed anything) and a
 * `suggestedTone` is provided, pressing Tab fills in the suggestion instead of
 * moving focus. Normal Tab focus-traversal behaviour is preserved whenever
 * there is nothing to complete (field has a value, or no suggestion).
 */
function ToneField({
  tone,
  setTone,
  suggestedTone,
}: {
  tone: string;
  setTone: (v: string) => void;
  suggestedTone?: string;
}) {
  const DEFAULT_SUGGESTION = "Professional and collaborative. Emphasise long-term partnership.";
  const suggestion = suggestedTone ?? DEFAULT_SUGGESTION;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Tab" && suggestion && !tone.trim()) {
        e.preventDefault();
        setTone(suggestion);
      }
    },
    [tone, suggestion, setTone]
  );

  return (
    <Textarea
      value={tone}
      onChange={(e) => setTone(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={suggestion}
      rows={2}
      className="text-sm resize-none"
    />
  );
}

// ---------------------------------------------------------------------------
// Stage 4 — Terms + template picker
// ---------------------------------------------------------------------------

interface TermsPanelProps {
  targetDiscount: string;
  setTargetDiscount: (v: string) => void;
  daysEarly: string;
  setDaysEarly: (v: string) => void;
  floorDiscount: string;
  setFloorDiscount: (v: string) => void;
  tone: string;
  setTone: (v: string) => void;
  /** Ghost-text suggestion for the tone field; Tab accepts it when field is empty. */
  suggestedTone?: string;
  templateId: string;
  setTemplateId: (v: string) => void;
  currentTerms?: {
    discount_pct: number | null;
    days_early: number | null;
    payment_terms?: string | null;
    mixed_terms?: string[] | null;
  } | null;
}

function TermsPanel({
  targetDiscount,
  setTargetDiscount,
  daysEarly,
  setDaysEarly,
  floorDiscount,
  setFloorDiscount,
  tone,
  setTone,
  suggestedTone,
  templateId,
  setTemplateId,
  currentTerms,
}: TermsPanelProps) {
  const { templates, isLoading: loadingTemplates } = useNegotiationTemplates();
  const [guardrailsOpen, setGuardrailsOpen] = useState(false);

  const handleTemplateChange = (id: string) => {
    setTemplateId(id);
    if (id === "none") return;
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    if (tpl.tone) setTone(tpl.tone);
    if (tpl.target_discount_pct != null) setTargetDiscount(String(tpl.target_discount_pct));
  };

  // Current vs Proposed comparison labels
  // Build the "Current" label from all available current-terms fields.
  //   - If there are mixed terms, show "Mixed: Net 30 / COD"
  //   - Otherwise show the payment_terms string (if any), then any discount/days
  const currentLabel = (() => {
    if (!currentTerms) return null;

    const parts: string[] = [];

    if (currentTerms.mixed_terms && currentTerms.mixed_terms.length > 1) {
      parts.push(`Mixed: ${currentTerms.mixed_terms.join(" / ")}`);
    } else if (currentTerms.payment_terms) {
      parts.push(currentTerms.payment_terms);
    }

    if (currentTerms.discount_pct != null) {
      parts.push(`${currentTerms.discount_pct.toFixed(2)}%`);
    }
    if (currentTerms.days_early != null) {
      parts.push(`paid within ${currentTerms.days_early} days`);
    }

    return parts.length > 0 ? parts.join(" · ") : null;
  })();

  const proposedLabel =
    targetDiscount || daysEarly
      ? [
          targetDiscount ? `${Number(targetDiscount).toFixed(2)}%` : null,
          daysEarly ? `within ${daysEarly} days` : null,
        ]
          .filter(Boolean)
          .join(" / ")
      : null;

  return (
    <div className="space-y-4">
      {/* Template picker — hidden until SHOW_TEMPLATES is re-enabled */}
      {SHOW_TEMPLATES && (
        <div className="space-y-1.5">
          <Label className="text-xs">Template (optional)</Label>
          {loadingTemplates ? (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading templates…
            </div>
          ) : (
            <Select value={templateId} onValueChange={handleTemplateChange}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="No template — fill manually" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-3 h-3 text-primary" />
                      {t.name}
                      <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1">
                        {t.kind}
                      </Badge>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Current vs Proposed comparison */}
      <div className="flex items-center gap-3 p-3 rounded-lg border border-border/60 bg-muted/20 text-xs">
        <div className="flex-1 min-w-0">
          <p className="text-muted-foreground mb-0.5">Current</p>
          <p className="font-medium text-foreground">{currentLabel ?? "No prior terms on file"}</p>
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-muted-foreground mb-0.5">Proposed</p>
          <p className="font-medium text-foreground">{proposedLabel ?? "—"}</p>
        </div>
      </div>

      <Separator />

      {/* Primary terms — Target Discount + Pay within (days) */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="nd-target-discount" className="text-xs">
            Target Discount %
          </Label>
          <Input
            id="nd-target-discount"
            value={targetDiscount}
            onChange={(e) => setTargetDiscount(e.target.value)}
            type="number"
            min="0"
            step="0.01"
            placeholder="2.00"
            className="h-9 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="nd-days-early" className="text-xs">
            Early Pay Days
          </Label>
          <Input
            id="nd-days-early"
            value={daysEarly}
            onChange={(e) => setDaysEarly(e.target.value)}
            type="number"
            min="1"
            placeholder="20"
            className="h-9 text-sm"
          />
        </div>
      </div>

      {/* Guardrails (advanced) — collapsible */}
      <Collapsible open={guardrailsOpen} onOpenChange={setGuardrailsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronDown
              className={cn("w-3.5 h-3.5 transition-transform", guardrailsOpen && "rotate-180")}
            />
            Guardrails (advanced)
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="nd-floor-discount" className="text-xs">
              Floor Discount % (min acceptable)
            </Label>
            <Input
              id="nd-floor-discount"
              value={floorDiscount}
              onChange={(e) => setFloorDiscount(e.target.value)}
              type="number"
              min="0"
              step="0.01"
              placeholder="1.00"
              className="h-9 text-sm"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Tone */}
      <div className="space-y-1.5">
        <Label className="text-xs">Negotiation Tone</Label>
        <ToneField tone={tone} setTone={setTone} suggestedTone={suggestedTone} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage 5 — Recipients + subject confirmation
// ---------------------------------------------------------------------------

interface SendPanelProps {
  recipients: string[];
  setRecipients: (r: string[]) => void;
  subject: string;
  setSubject: (v: string) => void;
  fromEmail: string;
  setFromEmail: (v: string) => void;
  targetDiscount: string;
  daysEarly: string;
  floorDiscount: string;
}

function SendPanel({
  recipients,
  setRecipients,
  subject,
  setSubject,
  fromEmail,
  setFromEmail,
  targetDiscount,
  daysEarly,
  floorDiscount,
}: SendPanelProps) {
  const fromEmailTrimmed = fromEmail.trim();
  const isFromEmailInvalid = fromEmailTrimmed.length > 0 && !isValidEmail(fromEmailTrimmed);

  return (
    <div className="space-y-4">
      {/* Summary chip row */}
      <div className="flex flex-wrap gap-2 p-3 rounded-lg border border-border/60 bg-muted/20">
        {targetDiscount && (
          <span className="text-xs border border-border/60 rounded px-1.5 py-0.5 bg-muted/30 text-foreground">
            Target {formatDiscount(Number(targetDiscount))}
          </span>
        )}
        {daysEarly && (
          <span className="text-xs border border-border/60 rounded px-1.5 py-0.5 bg-muted/30 text-foreground">
            Pay within {daysEarly} days
          </span>
        )}
        {floorDiscount && (
          <span className="text-xs border border-border/60 rounded px-1.5 py-0.5 bg-muted/30 text-muted-foreground">
            Floor {formatDiscount(Number(floorDiscount))}
          </span>
        )}
      </div>

      <RecipientsInput recipients={recipients} onChange={setRecipients} />

      <div className="space-y-1.5">
        <Label htmlFor="nd-from-email" className="text-xs">
          Send from
        </Label>
        <Input
          id="nd-from-email"
          type="email"
          placeholder="negotiations@yourcompany.com"
          value={fromEmail}
          onChange={(e) => setFromEmail(e.target.value)}
          className={`h-9 text-sm${isFromEmailInvalid ? " border-destructive focus-visible:ring-destructive" : ""}`}
          aria-label="Send from email address"
        />
        {isFromEmailInvalid && (
          <p className="text-[11px] text-destructive">Enter a valid email address</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="nd-subject" className="text-xs">
          Subject *
        </Label>
        <Input
          id="nd-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Early payment discount proposal"
          className="h-9 text-sm"
          required
        />
      </div>

      {recipients.length > 1 && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Mail className="w-3 h-3" />
          Primary: {recipients[0]} · CC: {recipients.slice(1).join(", ")}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Simple RFC-5322 email check (matches CampaignDialog). */
function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function defaultSubject(vendorName: string | null | undefined): string {
  return vendorName ? `Early payment discount — ${vendorName}` : "Early payment discount proposal";
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

export function NewNegotiationDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  defaultEmail,
  defaultSupplierName,
  defaultVendorName,
  defaultTone,
  negotiationCase,
  suggestedTerms,
  vendorOptions,
  onVendorSelect,
  currentTerms,
}: NewNegotiationDialogProps) {
  const { settings, updateSettings } = useSettings();
  const { upsertContact, isUpserting } = useVendorContacts();
  const [step, setStep] = useState<Step>(0);

  // --- Vendor picker state (used when no defaultVendorName is provided) ---
  const [dialogSelectedVendor, setDialogSelectedVendor] = useState<string>("");

  // --- Contact-email gate state (step 0) ---
  // Used when the vendor has no pre-existing contact email.
  const [inlineEmail, setInlineEmail] = useState("");
  const [inlineEmailSaved, setInlineEmailSaved] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);

  // --- Stage 4 state ---
  // suggestedTerms takes priority; fall back to global settings defaults when absent
  const [targetDiscount, setTargetDiscount] = useState(
    suggestedTerms?.target_discount_pct != null
      ? String(suggestedTerms.target_discount_pct)
      : settings?.default_target_discount_pct != null
        ? String(settings.default_target_discount_pct)
        : ""
  );
  const [daysEarly, setDaysEarly] = useState(
    suggestedTerms?.target_days_early != null
      ? String(suggestedTerms.target_days_early)
      : settings?.default_target_days_early != null
        ? String(settings.default_target_days_early)
        : ""
  );
  const [floorDiscount, setFloorDiscount] = useState(
    suggestedTerms?.floor_discount_pct != null ? String(suggestedTerms.floor_discount_pct) : ""
  );
  // defaultTone prop takes priority; fall back to global settings default tone
  const [tone, setTone] = useState(defaultTone ?? settings?.default_negotiation_tone ?? "");
  const [templateId, setTemplateId] = useState("none");

  // --- Stage 5 state ---
  const [recipients, setRecipients] = useState<string[]>(defaultEmail ? [defaultEmail] : []);
  const [fromEmail, setFromEmail] = useState("");
  const [supplierName, setSupplierName] = useState(defaultSupplierName ?? "");
  const [subject, setSubject] = useState(() =>
    defaultVendorName ? defaultSubject(defaultVendorName) : defaultSubject(null)
  );

  // --- Track open transition to reset step only on open ---
  const wasOpen = useRef(false);

  // Reset step to 0 only when dialog transitions from closed → open
  useEffect(() => {
    if (open && !wasOpen.current) {
      setStep(0);
      setDialogSelectedVendor("");
      setTemplateId("none");
      setFromEmail(settings?.negotiation_from_email ?? "");
      setInlineEmail("");
      setInlineEmailSaved(false);
    }
    wasOpen.current = open;
  }, [open, settings?.negotiation_from_email]);

  // Sync editable fields when suggestedTerms / defaultEmail / defaultTone /
  // defaultVendorName change (i.e. when the parent rebuilds after a vendor pick).
  useEffect(() => {
    setTargetDiscount(
      suggestedTerms?.target_discount_pct != null
        ? String(suggestedTerms.target_discount_pct)
        : settings?.default_target_discount_pct != null
          ? String(settings.default_target_discount_pct)
          : ""
    );
    setDaysEarly(
      suggestedTerms?.target_days_early != null
        ? String(suggestedTerms.target_days_early)
        : settings?.default_target_days_early != null
          ? String(settings.default_target_days_early)
          : ""
    );
    setFloorDiscount(
      suggestedTerms?.floor_discount_pct != null ? String(suggestedTerms.floor_discount_pct) : ""
    );
    setTone(defaultTone ?? settings?.default_negotiation_tone ?? "");
    setRecipients(defaultEmail ? [defaultEmail] : []);
    setSupplierName(defaultSupplierName ?? "");
    setSubject(defaultSubject(defaultVendorName));
    // Reset inline-email gate when vendor/email changes
    setInlineEmail("");
    setInlineEmailSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedTerms, defaultEmail, defaultTone, defaultVendorName, settings]);

  const reset = () => {
    setStep(0);
    setDialogSelectedVendor("");
    setTargetDiscount(
      suggestedTerms?.target_discount_pct != null
        ? String(suggestedTerms.target_discount_pct)
        : settings?.default_target_discount_pct != null
          ? String(settings.default_target_discount_pct)
          : ""
    );
    setDaysEarly(
      suggestedTerms?.target_days_early != null
        ? String(suggestedTerms.target_days_early)
        : settings?.default_target_days_early != null
          ? String(settings.default_target_days_early)
          : ""
    );
    setFloorDiscount(
      suggestedTerms?.floor_discount_pct != null ? String(suggestedTerms.floor_discount_pct) : ""
    );
    setTone(defaultTone ?? settings?.default_negotiation_tone ?? "");
    setTemplateId("none");
    setRecipients(defaultEmail ? [defaultEmail] : []);
    setFromEmail(settings?.negotiation_from_email ?? "");
    setSupplierName(defaultSupplierName ?? "");
    setSubject(defaultSubject(defaultVendorName));
    setInlineEmail("");
    setInlineEmailSaved(false);
  };

  const close = () => {
    if (!isSubmitting) {
      reset();
      onOpenChange(false);
    }
  };

  // Contact-email gate: the vendor is "known" when a name is preselected or the user
  // has picked one from the picker. When known, we require an email before advancing.
  // Use || to collapse empty strings to null (dialogSelectedVendor starts as "").
  const activeVendorName = (defaultVendorName || null) ?? (dialogSelectedVendor || null) ?? null;
  // The effective email: either from the prop (already saved) or from the inline save.
  const effectiveEmail = defaultEmail ?? (inlineEmailSaved ? inlineEmail.trim() : null);
  // Gate applies only when a vendor name is known (no gate for the "pick vendor" flow
  // before a vendor has been selected).
  const emailGateRequired = activeVendorName != null && !effectiveEmail;
  const inlineEmailTrimmed = inlineEmail.trim();
  const inlineEmailValid = isValidEmail(inlineEmailTrimmed);

  const handleSaveInlineEmail = async () => {
    if (!inlineEmailValid || !activeVendorName) return;
    setIsSavingEmail(true);
    try {
      await upsertContact(activeVendorName, { supplier_email: inlineEmailTrimmed });
      // Pre-populate recipients so the Send step is ready
      setRecipients([inlineEmailTrimmed]);
      setInlineEmailSaved(true);
    } finally {
      setIsSavingEmail(false);
    }
  };

  // "Next" from Case step is allowed only when the email gate is satisfied.
  const canAdvanceToStep1 = !emailGateRequired;
  const fromEmailTrimmed = fromEmail.trim();
  const isFromEmailInvalid = fromEmailTrimmed.length > 0 && !isValidEmail(fromEmailTrimmed);
  const canSubmit =
    recipients.length > 0 && subject.trim() !== "" && !isSubmitting && !isFromEmailInvalid;

  const handleSubmit = async () => {
    const primaryEmail = recipients[0] ?? "";
    const targetDiscountNum = targetDiscount ? Number(targetDiscount) : null;
    const daysEarlyNum = daysEarly ? Number(daysEarly) : null;
    const floorDiscountNum = floorDiscount ? Number(floorDiscount) : null;

    const derivedTargetApr =
      targetDiscountNum != null && daysEarlyNum != null
        ? discountToApr(targetDiscountNum, daysEarlyNum)
        : null;

    // Derive floor_apr from floor discount + days early; fall back to suggestedTerms
    const floorAprNum =
      floorDiscountNum != null && daysEarlyNum != null
        ? discountToApr(floorDiscountNum, daysEarlyNum)
        : (suggestedTerms?.floor_apr ?? null);

    // Persist the "Send from" address if set or changed
    if (fromEmailTrimmed && fromEmailTrimmed !== (settings?.negotiation_from_email ?? "")) {
      await updateSettings({ negotiation_from_email: fromEmailTrimmed });
    }

    await onSubmit({
      supplier_email: primaryEmail,
      supplier_name: supplierName || null,
      vendor_name: defaultVendorName ?? dialogSelectedVendor ?? null,
      subject,
      target_discount_pct: targetDiscountNum,
      target_days_early: daysEarlyNum,
      floor_discount_pct: floorDiscountNum,
      floor_apr: floorAprNum,
      target_apr: derivedTargetApr,
      max_acceptable_apr: floorAprNum,
      tone: tone || null,
      recipients,
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground">Start Negotiation</DialogTitle>
          {defaultVendorName && (
            <DialogDescription className="text-muted-foreground">
              {defaultVendorName}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="mt-1">
          <StepIndicator step={step} />

          {step === 0 && (
            <div className="space-y-3">
              {/* Vendor picker — shown when no vendor is preselected */}
              {!defaultVendorName && vendorOptions && vendorOptions.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Select Vendor</Label>
                  <Select
                    value={dialogSelectedVendor}
                    onValueChange={(name) => {
                      setDialogSelectedVendor(name);
                      onVendorSelect?.(name);
                    }}
                  >
                    <SelectTrigger className="h-9 text-sm" aria-label="Select vendor">
                      <SelectValue placeholder="Choose a vendor…" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendorOptions.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Contact-email gate — shown when vendor is known but has no email */}
              {emailGateRequired && (
                <div
                  className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2"
                  data-testid="email-gate"
                >
                  <div className="flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                      No contact email on file for {activeVendorName}
                    </span>
                  </div>
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">
                    Enter a contact email to proceed, or close and add one from the vendor roster.
                  </p>
                  <div className="flex gap-2 items-start">
                    <div className="flex-1 space-y-0.5">
                      <Input
                        type="email"
                        placeholder="supplier@example.com"
                        value={inlineEmail}
                        onChange={(e) => setInlineEmail(e.target.value)}
                        className={`h-8 text-xs${
                          inlineEmailTrimmed && !inlineEmailValid
                            ? " border-destructive focus-visible:ring-destructive"
                            : ""
                        }`}
                        disabled={isSavingEmail || isUpserting}
                        aria-label="Contact email for vendor"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && inlineEmailValid) handleSaveInlineEmail();
                        }}
                      />
                      {inlineEmailTrimmed && !inlineEmailValid && (
                        <p className="text-[10px] text-destructive">Enter a valid email address</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs flex-shrink-0"
                      onClick={handleSaveInlineEmail}
                      disabled={!inlineEmailValid || isSavingEmail || isUpserting}
                      aria-label="Save contact email"
                    >
                      {isSavingEmail || isUpserting ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Confirmation badge once email saved inline */}
              {inlineEmailSaved && (
                <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
                  <Check className="w-3.5 h-3.5" />
                  Contact email saved — {inlineEmailTrimmed}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Review the data-backed case for initiating this negotiation.
              </p>
              <CasePanel negotiationCase={negotiationCase ?? null} />
            </div>
          )}

          {step === 1 && (
            <TermsPanel
              targetDiscount={targetDiscount}
              setTargetDiscount={setTargetDiscount}
              daysEarly={daysEarly}
              setDaysEarly={setDaysEarly}
              floorDiscount={floorDiscount}
              setFloorDiscount={setFloorDiscount}
              tone={tone}
              setTone={setTone}
              suggestedTone={defaultTone ?? undefined}
              templateId={templateId}
              setTemplateId={setTemplateId}
              currentTerms={currentTerms}
            />
          )}

          {step === 2 && (
            <SendPanel
              recipients={recipients}
              setRecipients={setRecipients}
              subject={subject}
              setSubject={setSubject}
              fromEmail={fromEmail}
              setFromEmail={setFromEmail}
              targetDiscount={targetDiscount}
              daysEarly={daysEarly}
              floorDiscount={floorDiscount}
            />
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-5 pt-3 border-t border-border">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                if (step === 0) close();
                else setStep((s) => (s - 1) as Step);
              }}
              disabled={isSubmitting}
            >
              {step === 0 ? (
                "Cancel"
              ) : (
                <>
                  <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                  Back
                </>
              )}
            </Button>

            {step < 2 ? (
              <Button
                type="button"
                size="sm"
                onClick={() => setStep((s) => (s + 1) as Step)}
                disabled={step === 0 ? !canAdvanceToStep1 : false}
              >
                Next
                <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Starting…
                  </>
                ) : (
                  "Start Negotiation"
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
